import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import SearchIcon from '@mui/icons-material/Search';

import AuthContext from '../../authAndConnections/auth';
import AxiosGlobal from '../../authAndConnections/axiosGlobalUrl';
import { useDocFormat } from '../docFormat';
import { palletCodeKey } from './palletEditor';

const sqmOf = (p) => (p.items || []).reduce((a, it) => a + (Number(it.sqm) || 0), 0);

// The button and the dialog a packing list takes pallets from a label form with. Pick a form (by its number, its destination, a pallet code ...):
// its free pallets are ticked at once, so one press brings them all ("Add N pallets"); untick what should stay behind. A form's row also has
// "Import N free" for the same in one press. A pallet on another list cannot be taken; one already on this list is shown as such. The form's
// origin and destination fill the list's when the list has none. The list's own save checks everything again.
//   onAdd(records, { originAddress, destinationAddress })
export function LabelFormPickerButton({ branchId, listId = null, heldCodes, hasOrigin, hasDestination, onAdd, touch, T }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="small" variant="outlined" onClick={() => setOpen(true)} data-testid="pl-from-form" startIcon={<LibraryBooksOutlinedIcon sx={{ fontSize: 15 }} />}
        sx={{ textTransform: 'none', fontSize: '0.72rem', mt: 1, ...(touch ? { width: '100%', minHeight: 48, fontSize: '0.85rem' } : {}) }}>
        {t('mis.lfFromForm')}
      </Button>
      {open && (
        <LabelFormPicker open={open} onClose={() => setOpen(false)} branchId={branchId} listId={listId} heldCodes={heldCodes}
          hasOrigin={hasOrigin} hasDestination={hasDestination} onAdd={(records, places) => { onAdd(records, places); setOpen(false); }} T={T} />
      )}
    </>
  );
}

function LabelFormPicker({ open, onClose, branchId, listId, heldCodes, hasOrigin, hasDestination, onAdd, T }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const fmt = useDocFormat();
  const authCtx = useContext(AuthContext);
  const axiosGlobal = useContext(AxiosGlobal);

  const [search, setSearch] = useState('');
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);           // the form picked, with its pallets
  const [ticked, setTicked] = useState(() => new Set());
  const [usePlaces, setUsePlaces] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  const held = useMemo(() => new Set((heldCodes || []).map(palletCodeKey).filter(Boolean)), [heldCodes]);
  // where a pallet stands for THIS list
  const standing = (p) => {
    if (held.has(palletCodeKey(p.reference)) || (listId && p.list && String(p.list._id) === String(listId))) return 'here';
    if (p.list) return 'other';
    return 'free';
  };

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      authCtx.jwtInst({ method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/label-forms`, params: { branchId, limit: 60, ...(search.trim() ? { search: search.trim() } : {}) } })
        .then((res) => { if (!cancelled) setForms(res.data.data || []); })
        .catch(() => { if (!cancelled) setError(t('mis.lfLoadFailed')); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [open, search, branchId, authCtx, axiosGlobal, t]);

  const fetchForm = async (id) => {
    const res = await authCtx.jwtInst({ method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/label-forms/${id}` });
    return res.data;
  };
  const placesOf = (f) => (usePlaces ? { originAddress: hasOrigin ? '' : (f.originAddress || ''), destinationAddress: hasDestination ? '' : (f.destinationAddress || '') } : {});

  const pick = async (row) => {
    setError(''); setBusyId(row._id);
    try {
      const full = await fetchForm(row._id);
      setForm(full);
      setTicked(new Set((full.pallets || []).filter((p) => standing(p) === 'free').map((p) => p._id)));   // the free ones, ticked: imported in one press
    } catch (_) { setError(t('mis.lfLoadFailed')); } finally { setBusyId(null); }
  };
  // straight from the list of forms: every free pallet of it, at once
  const importFree = async (row) => {
    setError(''); setBusyId(row._id);
    try {
      const full = await fetchForm(row._id);
      const free = (full.pallets || []).filter((p) => standing(p) === 'free');
      if (!free.length) { setError(t('mis.lfNothingFree', { number: full.docNumber })); return; }
      onAdd(free, placesOf(full));
    } catch (_) { setError(t('mis.lfLoadFailed')); } finally { setBusyId(null); }
  };
  const addTicked = () => {
    const records = (form.pallets || []).filter((p) => ticked.has(p._id) && standing(p) === 'free');
    if (records.length) onAdd(records, placesOf(form));
  };
  const toggle = (id) => setTicked((cur) => { const next = new Set(cur); if (next.has(id)) next.delete(id); else next.add(id); return next; });

  const freeIn = form ? (form.pallets || []).filter((p) => standing(p) === 'free') : [];
  const placesLine = form && [form.originAddress, form.destinationAddress].filter(Boolean).join(' → ');

  return (
    <Dialog open={open} onClose={onClose} fullScreen={isXs} maxWidth="sm" fullWidth PaperProps={{ 'data-testid': 'lf-picker', sx: { borderRadius: isXs ? 0 : '14px', backgroundImage: 'none' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, borderBottom: `1px solid ${T.BD}` }}>
        {form && (
          <IconButton size="small" onClick={() => setForm(null)} aria-label={t('common.back')} data-testid="lf-picker-back"><ArrowBackIcon sx={{ fontSize: 18 }} /></IconButton>
        )}
        <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', flex: 1, color: T.TEXT_PRI }}>
          {form ? t('mis.lfDetailTitle', { number: form.docNumber }) : t('mis.lfPickerTitle')}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label={t('common.close')}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
      </Box>

      <Box sx={{ px: 2, py: 1.5, minHeight: 280, maxHeight: isXs ? 'none' : '62vh', overflowY: 'auto', flex: isXs ? 1 : 'none' }}>
        {!form ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: T.CTRL_BG, border: `1px solid ${T.BD}`, borderRadius: '9px', px: 1, height: isXs ? 44 : 34, mb: 1.25 }}>
              <SearchIcon sx={{ fontSize: 16, color: T.TEXT_TER }} />
              <InputBase autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('mis.lfSearchPlaceholder')}
                inputProps={{ 'data-testid': 'lf-picker-search' }} sx={{ flex: 1, fontSize: isXs ? '16px' : '0.8rem' }} />
            </Box>
            {loading && !forms.length ? (
              <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={20} /></Box>
            ) : !forms.length ? (
              <Typography sx={{ fontSize: '0.8rem', color: T.TEXT_SEC, textAlign: 'center', py: 4 }}>{search ? t('mis.lfNoneMatch') : t('mis.lfNoneYet')}</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {forms.map((row) => {
                  const c = row.counts || { pallets: 0, free: 0 };
                  return (
                    <Box key={row._id} data-lf-pick={row.docNumber}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: '10px', border: `1px solid ${T.BD}`, flexWrap: isXs ? 'wrap' : 'nowrap' }}>
                      <Box onClick={() => pick(row)} sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
                        <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: T.TEXT_PRI }}>
                          #{row.docNumber} <Box component="span" sx={{ fontWeight: 400, fontSize: '0.7rem', color: T.TEXT_TER }}>{fmt.date(row.issueDate, row.dateSystem)}</Box>
                        </Typography>
                        <Typography noWrap sx={{ fontSize: '0.72rem', color: T.TEXT_SEC }}>
                          {[t('mis.lfFreeOfTotal', { free: c.free, total: c.pallets }), row.destinationAddress].filter(Boolean).join(' · ')}
                        </Typography>
                      </Box>
                      <Button size="small" onClick={() => pick(row)} disabled={busyId === row._id} data-testid="lf-pick-choose"
                        sx={{ textTransform: 'none', fontSize: '0.7rem', minHeight: isXs ? 40 : 0 }}>{t('mis.lfChoosePallets')}</Button>
                      <Button size="small" variant="contained" onClick={() => importFree(row)} disabled={!c.free || busyId === row._id} data-testid="lf-pick-import"
                        startIcon={busyId === row._id ? <CircularProgress size={12} color="inherit" /> : null}
                        sx={{ textTransform: 'none', fontSize: '0.7rem', minHeight: isXs ? 40 : 0, flexShrink: 0 }}>{t('mis.lfImportFree', { count: c.free })}</Button>
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        ) : (
          <>
            {placesLine && <Typography sx={{ fontSize: '0.74rem', color: T.TEXT_SEC, mb: 1 }}>{placesLine}</Typography>}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
              <Typography sx={{ fontSize: '0.72rem', color: T.TEXT_TER, flex: 1 }}>{t('mis.lfFreeOfTotal', { free: freeIn.length, total: (form.pallets || []).length })}</Typography>
              {freeIn.length > 0 && (
                <Button size="small" onClick={() => setTicked(ticked.size === freeIn.length ? new Set() : new Set(freeIn.map((p) => p._id)))} data-testid="lf-pick-all"
                  sx={{ textTransform: 'none', fontSize: '0.7rem' }}>{ticked.size === freeIn.length ? t('mis.lblClearSelection') : t('mis.lblSelectAll')}</Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {(form.pallets || []).map((p) => {
                const s = standing(p);
                const label = s === 'here' ? t('mis.lfAlreadyHere') : s === 'other' ? t('mis.lblOnListChip', { list: p.list.docNumber }) : t('mis.lblAvailable');
                return (
                  <Box key={p._id} data-lf-pick-pallet={p.reference} data-standing={s}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5, py: 0.25, borderRadius: '8px', opacity: s === 'free' ? 1 : 0.55 }}>
                    <Checkbox size="small" checked={s === 'free' && ticked.has(p._id)} disabled={s !== 'free'} onChange={() => toggle(p._id)}
                      inputProps={{ 'aria-label': p.reference }} sx={isXs ? { width: 44, height: 44 } : undefined} />
                    <Typography dir="ltr" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 700, color: T.TEXT_PRI, minWidth: 62 }}>{p.reference}</Typography>
                    <Typography noWrap sx={{ fontSize: '0.72rem', color: T.TEXT_SEC, flex: 1, minWidth: 0 }}>
                      {[p.palletId, p.processingType, `${sqmOf(p).toFixed(4)} ${t('mis.plColSqm')}`].filter(Boolean).join(' · ')}
                    </Typography>
                    <Typography noWrap sx={{ fontSize: '0.66rem', fontWeight: 700, color: s === 'free' ? 'success.main' : T.TEXT_TER }}>{label}</Typography>
                  </Box>
                );
              })}
            </Box>
          </>
        )}
        {error && <Typography role="alert" data-testid="lf-picker-error" sx={{ fontSize: '0.74rem', color: 'error.main', mt: 1 }}>{error}</Typography>}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', px: 2, py: 1.5, borderTop: `1px solid ${T.BD}`, pb: { xs: 'max(12px, env(safe-area-inset-bottom))', sm: 1.5 } }}>
        {(!hasOrigin || !hasDestination) && (
          <FormControlLabel sx={{ flex: 1, minWidth: 0, m: 0 }}
            control={<Checkbox size="small" checked={usePlaces} onChange={(e) => setUsePlaces(e.target.checked)} inputProps={{ 'data-testid': 'lf-pick-places' }} />}
            label={<Typography sx={{ fontSize: '0.72rem', color: T.TEXT_SEC }}>{t('mis.lfUsePlaces')}</Typography>} />
        )}
        <Box sx={{ marginInlineStart: 'auto', display: 'flex', gap: 1 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none', minHeight: isXs ? 48 : 0 }}>{t('common.cancel')}</Button>
          {form && (
            <Button variant="contained" onClick={addTicked} disabled={!ticked.size} data-testid="lf-pick-add" sx={{ textTransform: 'none', minHeight: isXs ? 48 : 0 }}>
              {t('mis.lfAddTicked', { count: ticked.size })}
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}

export default LabelFormPickerButton;
